import { supabase } from '../lib/supabase';

export interface AuthUser {
  /** auth_id (UUID do Supabase Auth) */
  id: string;
  /** id da linha na tabela 'usuarios' (UUID) */
  db_id?: string;
  email: string;
  nome: string;
  setor: string;
  role: string;
  ativo: boolean;
  picture?: string;
  cargo?: string;
  departamento?: string;
  telefone?: string;
  ramal?: string;
}

type UsuarioRow = {
  id: string;            // UUID da tabela usuarios
  auth_id: string;       // UUID do Auth
  usuario?: string | null;
  nome: string;
  email: string;
  setor: string;
  role: string;
  ativo: boolean;
  picture?: string | null;
  cargo?: string | null;
  departamento?: string | null;
  telefone?: string | null;
  ramal?: string | null;
};

const normalizeEmail = (s: string) => (s || '').trim().toLowerCase();
const normalizeText  = (s: string) => (s || '').trim();

const SupabaseClient = {
  async getUserByAuthId(authId: string): Promise<UsuarioRow | null> {
    const { data, error } = await supabase
      .from('usuarios')
      .select('*')
      .eq('auth_id', authId)
      .maybeSingle();
    if (error) throw error;
    return data as unknown as UsuarioRow | null;
  },

  async upsertUser(row: Partial<UsuarioRow>) {
    // Requer índice/constraint de unicidade em auth_id (recomendado)
    const { data, error } = await supabase
      .from('usuarios')
      .upsert(row, { onConflict: 'auth_id' })
      .select()
      .maybeSingle();
    if (error) throw error;
    return data as unknown as UsuarioRow | null;
  },

  async createUser(row: Partial<UsuarioRow>) {
    console.log('[SUPABASE-CLIENT] Creating user with data:', {
      auth_id: row.auth_id,
      email: row.email,
      nome: row.nome,
      role: row.role,
      setor: row.setor
    });

    const { data, error } = await supabase
      .from('usuarios')
      .insert(row)
      .select()
      .single();

    if (error) {
      console.error('[SUPABASE-CLIENT] Error creating user:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    console.log('[SUPABASE-CLIENT] User created successfully:', data);
    return data as unknown as UsuarioRow;
  },
};

function mapAuthError(error: any): Error {
  const code = error?.code;
  const status = error?.status;
  let msg = error?.message || 'Falha na autenticação.';

  if (code === 'invalid_credentials' || status === 400) {
    msg = 'E-mail ou senha inválidos.';
  } else if (code === 'email_not_confirmed') {
    msg = 'E-mail não confirmado. Verifique sua caixa de entrada.';
  } else if (code === 'over_email_send_rate_limit') {
    msg = 'Muitas tentativas. Tente novamente em alguns minutos.';
  }
  const e = new Error(msg);
  (e as any).status = status;
  (e as any).code = code;
  return e;
}

async function ensureProfileFor(authUser: { id: string; email?: string | null; user_metadata?: any }, fallback?: {
  nome?: string; setor?: string; role?: string; picture?: string;
}) {
  console.log('[SUPABASE-AUTH] ensureProfileFor called for auth_id:', authUser.id);

  const existing = await SupabaseClient.getUserByAuthId(authUser.id);
  if (existing) {
    console.log('[SUPABASE-AUTH] User profile already exists:', existing.email);

    const latestPicture =
      authUser.user_metadata?.avatar_url ||
      authUser.user_metadata?.picture ||
      null;

    if (latestPicture && latestPicture !== existing.picture) {
      await supabase
        .from('usuarios')
        .update({ picture: latestPicture })
        .eq('id', existing.id);
      existing.picture = latestPicture;
    }

    return existing;
  }

  console.log('[SUPABASE-AUTH] User profile not found, creating new profile...');

  const email = normalizeEmail(authUser.email || '');
  const nomeBase = fallback?.nome || authUser.user_metadata?.full_name || authUser.user_metadata?.name || email.split('@')[0] || 'Usuário';
  const setor = fallback?.setor || 'Geral';
  const role = fallback?.role || 'colaborador';
  const picture =
    fallback?.picture ||
    authUser.user_metadata?.avatar_url ||
    authUser.user_metadata?.picture ||
    null;

  const userToCreate = {
    auth_id: authUser.id,
    email,
    nome: nomeBase,
    setor,
    role,
    ativo: true,
    usuario: email.split('@')[0],
    picture,
  };

  console.log('[SUPABASE-AUTH] Creating user profile:', {
    email: userToCreate.email,
    nome: userToCreate.nome,
    setor: userToCreate.setor,
    role: userToCreate.role
  });

  return await SupabaseClient.createUser(userToCreate);
}

export class SupabaseAuth {
  // Sign up with email and password
  static async signUp(
    email: string,
    password: string,
    userData: { nome: string; setor: string; role: string }
  ) {
    const cleanEmail = normalizeEmail(email);
    const cleanPass  = normalizeText(password);

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: cleanEmail,
      password: cleanPass,
    });
    if (authError) throw mapAuthError(authError);

    // Quando confirmação por e-mail é exigida, authData.user pode vir null.
    const user = authData.user;
    if (!user) {
      return {
        authUser: null,
        userRecord: null,
        needsEmailConfirmation: true,
      };
    }

    const userRecord = await ensureProfileFor(user, {
      nome: userData.nome,
      setor: userData.setor,
      role: userData.role,
    });

    return { authUser: user, userRecord };
  }

  // Sign in with email and password (supports username or email)
  static async signIn(emailOrUsername: string, password: string) {
    let cleanEmail = normalizeEmail(emailOrUsername);
    const cleanPass = normalizeText(password);

    // Se não contém @, assume que é um username e busca o email no banco
    if (!cleanEmail.includes('@')) {
      try {
        const { data: userRecord, error } = await supabase
          .from('usuarios')
          .select('email, ativo, auth_id')
          .eq('usuario', cleanEmail)
          .maybeSingle();

        if (error) throw error;

        if (!userRecord) {
          throw new Error('Usuário não encontrado.');
        }

        if (!userRecord.ativo) {
          throw new Error('Usuário inativo.');
        }

        cleanEmail = userRecord.email;
      } catch (error) {
        console.error('[SUPABASE-AUTH] Error looking up username:', error);
        throw new Error('Usuário ou senha inválidos.');
      }
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: cleanEmail,
      password: cleanPass,
    });
    if (error) throw mapAuthError(error);
    if (!data.user) throw new Error('Login failed');

    // Garante perfil
    const userRow = await ensureProfileFor(data.user);

    if (!userRow?.ativo) {
      await supabase.auth.signOut();
      throw new Error('Usuário inativo.');
    }

    const authUser: AuthUser = {
      id: data.user.id,
      db_id: userRow.id,
      email: userRow.email,
      nome: userRow.nome,
      setor: userRow.setor,
      role: userRow.role,
      ativo: userRow.ativo,
      picture: data.user.user_metadata?.avatar_url ?? userRow.picture ?? undefined,
      cargo: userRow.cargo ?? undefined,
      departamento: userRow.departamento ?? undefined,
      telefone: userRow.telefone ?? undefined,
      ramal: userRow.ramal ?? undefined,
    };

    return { authUser, userProfile: authUser };
  }

  // Sign in with Google OAuth
  static async signInWithGoogle() {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { prompt: 'select_account' },
        },
      });
      if (error) {
        console.error('[SUPABASE-AUTH] Google OAuth error:', error);
        throw mapAuthError(error);
      }
      return data;
    } catch (error) {
      console.error('[SUPABASE-AUTH] Failed to initiate Google OAuth:', error);
      if (error instanceof Error && error.message.includes('Failed to fetch')) {
        throw new Error('Não foi possível conectar ao servidor de autenticação. Verifique sua conexão ou tente novamente mais tarde.');
      }
      throw error;
    }
  }

  // Get current user session
  static async getCurrentUser(): Promise<AuthUser | null> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    // garante/recupera perfil
    const userRow = await ensureProfileFor(user).catch(() => null);
    if (!userRow || !userRow.ativo) return null;

    return {
      id: user.id,           // sempre o auth_id (UUID)
      db_id: userRow.id,
      email: userRow.email,
      nome: userRow.nome,
      setor: userRow.setor,
      role: userRow.role,
      ativo: userRow.ativo,
      picture: user.user_metadata?.avatar_url ?? userRow.picture ?? undefined,
      cargo: userRow.cargo ?? undefined,
      departamento: userRow.departamento ?? undefined,
      telefone: userRow.telefone ?? undefined,
      ramal: userRow.ramal ?? undefined,
    };
  }

  // Sign out
  static async signOut() {
    const { error } = await supabase.auth.signOut();
    if (error) throw mapAuthError(error);
  }

  // Listen to auth changes
  static onAuthStateChange(callback: (user: AuthUser | null) => void) {
    return supabase.auth.onAuthStateChange(async (_event, session) => {
      const sUser = session?.user;
      if (!sUser) return callback(null);

      try {
        const userRow = await ensureProfileFor(sUser);
        if (!userRow?.ativo) return callback(null);

        callback({
          id: sUser.id,          // auth_id
          db_id: userRow.id,     // pk local
          email: userRow.email,
          nome: userRow.nome,
          setor: userRow.setor,
          role: userRow.role,
          ativo: userRow.ativo,
          picture: sUser.user_metadata?.avatar_url ?? userRow.picture ?? undefined,
          cargo: userRow.cargo ?? undefined,
          departamento: userRow.departamento ?? undefined,
          telefone: userRow.telefone ?? undefined,
          ramal: userRow.ramal ?? undefined,
        });
      } catch (err) {
        console.error('Error getting user profile:', err);
        callback(null);
      }
    });
  }

  // Check if user can publish to mural
  static canPublishMural(user: AuthUser): boolean {
    return user.setor === 'TI' ||
           user.setor === 'RH' ||
           user.role === 'admin' ||
           user.role === 'moderador';
  }

  // Check if user can moderate
  static canModerate(user: AuthUser): boolean {
    return user.role === 'admin' ||
           user.role === 'moderador' ||
           user.setor === 'TI';
  }

  // Check if user is admin
  static isAdmin(user: AuthUser): boolean {
    return user.role === 'admin' ||
           user.setor === 'TI' ||
           user.setor === 'RH' ||
           user.role === 'moderador';
  }
}

export default SupabaseAuth;
